import React, { useState } from "react";
import "./HealthDetailsModal.css";
import { NewsProcessorHealth } from "./useHealthPoller";

interface HealthDetailsModalProps {
  health: NewsProcessorHealth | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
  isRefreshing?: boolean;
}

export const HealthDetailsModal: React.FC<HealthDetailsModalProps> = ({
  health,
  isOpen,
  onClose,
  onRefresh,
  isRefreshing = false,
}) => {
  const [selectedTab, setSelectedTab] = useState<"status" | "details">(
    "status",
  );

  if (!isOpen) return null;

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "healthy":
        return "#10b981";
      case "unhealthy":
        return "#ef4444";
      case "unreachable":
        return "#6b7280";
      default:
        return "#6b7280";
    }
  };

  const getStatusLabel = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="health-modal-overlay" onClick={onClose}>
      <div
        className="health-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="health-modal-header">
          <h2>News Processor Health Status</h2>
          <button className="health-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {!health ? (
          <div className="health-modal-loading">
            <div className="spinner"></div>
            <p>Loading health status...</p>
          </div>
        ) : (
          <div className="health-modal-body">
            <div className="health-status-summary">
              <div className="status-indicator">
                <div
                  className="status-dot"
                  style={{
                    backgroundColor: getStatusColor(health.status),
                  }}
                ></div>
                <div>
                  <p className="status-label">Status:</p>
                  <p className="status-value">
                    {getStatusLabel(health.status)}
                  </p>
                </div>
              </div>

              <div className="status-metrics">
                <div className="metric">
                  <span className="metric-label">Response Time:</span>
                  <span className="metric-value">{health.responseTime}ms</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Last Checked:</span>
                  <span className="metric-value">
                    {new Date(health.lastChecked).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>

            {health.error && (
              <div className="health-error">
                <strong>Error:</strong> {health.error}
              </div>
            )}

            <div className="health-tabs">
              <button
                className={`tab ${selectedTab === "status" ? "active" : ""}`}
                onClick={() => setSelectedTab("status")}
              >
                Status
              </button>
              {health.details && (
                <button
                  className={`tab ${selectedTab === "details" ? "active" : ""}`}
                  onClick={() => setSelectedTab("details")}
                >
                  Details
                </button>
              )}
            </div>

            <div className="health-tab-content">
              {selectedTab === "status" && (
                <div className="status-content">
                  <div className="status-row">
                    <span className="label">Service:</span>
                    <span className="value">News Processor</span>
                  </div>
                  <div className="status-row">
                    <span className="label">Status:</span>
                    <span
                      className="value status"
                      style={{ color: getStatusColor(health.status) }}
                    >
                      {getStatusLabel(health.status)}
                    </span>
                  </div>
                  <div className="status-row">
                    <span className="label">Response Time:</span>
                    <span className="value">{health.responseTime}ms</span>
                  </div>
                  <div className="status-row">
                    <span className="label">Last Checked:</span>
                    <span className="value">
                      {new Date(health.lastChecked).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {selectedTab === "details" && health.details && (
                <div className="details-content">
                  <pre>{JSON.stringify(health.details, null, 2)}</pre>
                </div>
              )}
            </div>

            <div className="health-modal-footer">
              <button
                className="btn btn-primary"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? "Refreshing..." : "Manual Refresh"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
